'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import MuxPlayerModal from '@/app/film-detail-page/components/MuxPlayerModal';
import GoldenLaurel from '@/components/ui/GoldenLaurel';
import { filmService } from '@/lib/services/filmService';
import type { Film } from '@/lib/services/filmService';

const CONTENT_FLAG_KEYWORDS = [
  'nudity', 'violence', 'language', 'drug', 'drugs', 'alcohol', 'sexual content',
  'strong language', 'graphic violence', 'adult content', 'mature themes',
  'disturbing content', 'suicide', 'self-harm',
];

function getContentFlags(film: Film): string[] {
  const all = [...(film.themes || []), ...(film.genres || [])];
  return all.filter(t =>
    CONTENT_FLAG_KEYWORDS.some(kw => t.toLowerCase().includes(kw))
  );
}

export default function HeroSection() {
  const [heroFilms, setHeroFilms] = useState<Film[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);
  const [resumeSeconds, setResumeSeconds] = useState(0);

  useEffect(() => {
    filmService.getFeatured().then((films) => {
      setHeroFilms(films);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (heroFilms.length <= 1) return;
    const interval = setInterval(() => {
      setTransitioning(true);
      setTimeout(() => {
        setActiveIndex((i) => (i + 1) % heroFilms.length);
        setTransitioning(false);
      }, 300);
    }, 8000);
    return () => clearInterval(interval);
  }, [heroFilms]);

  const film = heroFilms[activeIndex];

  const handlePlayNow = async () => {
    if (film?.muxPlaybackId) {
      if (film.id) {
        filmService.recordWatchHistory(film.id);
        const ts = await filmService.getResumeTimestamp(film.id);
        setResumeSeconds(ts);
      }
      setShowPlayer(true);
    } else if (film?.id) {
      window.location.href = `/film-detail-page?id=${film.id}`;
    }
  };

  const handleTimeUpdate = (seconds: number) => {
    if (!film?.id) return;
    filmService.saveResumeTimestamp(film.id, seconds);
  };

  if (loading) {
    return (
      <div className="relative w-full h-[70vh] sm:h-[80vh] md:h-[88vh] min-h-[480px] max-h-[900px] skeleton-pulse" />
    );
  }

  if (!film) {
    return (
      <div className="relative w-full h-[70vh] sm:h-[80vh] md:h-[88vh] min-h-[480px] max-h-[900px] bg-gradient-to-b from-muted/30 to-background flex items-center justify-center">
        <div className="text-center px-4">
          <Icon name="FilmIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground text-sm">No featured films yet.</p>
          <p className="text-muted-foreground text-xs mt-1">Mark a film as Featured in the admin panel to display it here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-[70vh] sm:h-[80vh] md:h-[88vh] min-h-[480px] max-h-[900px] overflow-hidden">
      {/* Mux Video Player Modal */}
      {showPlayer && film?.muxPlaybackId && (
        <MuxPlayerModal
          playbackId={film.muxPlaybackId}
          title={film.title}
          contentRating={film.contentRating}
          contentFlags={getContentFlags(film)}
          filmId={film.id}
          startTime={resumeSeconds}
          onClose={() => setShowPlayer(false)}
          onTimeUpdate={handleTimeUpdate}
        />
      )}
      {/* Backdrop */}
      <div className={`absolute inset-0 transition-opacity duration-500 ${transitioning ? 'opacity-0' : 'opacity-100'}`}>
        <AppImage
          src={film.backdropUrl || film.posterUrl}
          alt={film.backdropAlt || film.posterAlt || `${film.title} backdrop`}
          fill
          priority
          sizes="100vw"
          className="object-cover object-center" />
      </div>
      {/* Gradients */}
      <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
      {/* Content */}
      <div className={`relative z-10 h-full flex flex-col justify-end pb-12 sm:pb-16 md:pb-20 px-4 sm:px-6 lg:px-10 2xl:px-16 max-w-screen-2xl mx-auto transition-all duration-500 ${transitioning ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}`}>
        {/* Festival badges */}
        <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
          {film.festivals?.slice(0, 2)?.map((f) =>
            <span key={`hero-festival-${f}`} className="laurel-badge text-[10px] sm:text-xs flex items-center gap-1">
              <GoldenLaurel size={13} />
              {f}
            </span>
          )}
        </div>

        {/* Title */}
        <h1 className="text-hero-xl font-bold text-foreground max-w-xs sm:max-w-lg md:max-w-2xl leading-tight mb-2 sm:mb-3">
          {film.title}
        </h1>

        {/* Meta row */}
        <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4 flex-wrap">
          <span className="text-xs sm:text-sm font-medium text-muted-foreground font-tabular">{film.year}</span>
          <span className="text-muted-foreground text-xs">·</span>
          <span className="text-xs sm:text-sm font-medium text-muted-foreground font-tabular">{film.runtimeDisplay || (film.runtimeMinutes ? `${Math.floor(film.runtimeMinutes / 60)}h ${film.runtimeMinutes % 60}m` : '')}</span>
          {film.genres?.slice(0, 2)?.map((g) => (
            <React.Fragment key={`hero-genre-${g}`}>
              <span className="text-muted-foreground text-xs">·</span>
              <span className="text-xs sm:text-sm font-medium text-muted-foreground">{g}</span>
            </React.Fragment>
          ))}
          <span className="text-xs text-muted-foreground border border-border px-1.5 py-0.5 rounded">{film.contentRating}</span>
        </div>

        {/* Tagline */}
        {film.tagline && (
          <p className="hidden sm:block text-sm sm:text-base lg:text-lg text-primary font-medium italic mb-2">&ldquo;{film.tagline}&rdquo;</p>
        )}

        {/* Main actors — listed above synopsis */}
        {film.castCrew && film.castCrew.filter((m: any) => m?.role?.toLowerCase() === 'actor' || m?.role?.toLowerCase() === 'cast' || m?.character).length > 0 && (
          <p className="text-xs sm:text-sm text-muted-foreground mb-1.5">
            <span className="font-medium">Starring: </span>
            {film.castCrew
              .filter((m: any) => m?.role?.toLowerCase() === 'actor' || m?.role?.toLowerCase() === 'cast' || m?.character)
              .slice(0, 4)
              .map((m: any) => m?.name)
              .filter(Boolean)
              .join(', ')}
          </p>
        )}

        {/* Synopsis */}
        {(film.synopsis || film.shortDescription) && (
          <p className="text-xs sm:text-sm text-muted-foreground max-w-xs sm:max-w-md lg:max-w-lg leading-relaxed mb-4 sm:mb-6 line-clamp-2 sm:line-clamp-3">
            {film.synopsis || film.shortDescription}
          </p>
        )}

        {/* Watch attributes */}
        {film.watchStyles && film.watchStyles.length > 0 && (
          <div className="hidden sm:flex flex-wrap gap-2 mb-4 sm:mb-6">
            {film.watchStyles.slice(0, 4).map((attr) =>
              <span key={`hero-attr-${attr}`} className="watch-attr-tag">{attr}</span>
            )}
          </div>
        )}

        {/* CTAs */}
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <button onClick={handlePlayNow} className="btn-primary text-sm sm:text-base px-4 sm:px-6 py-2.5 sm:py-3">
            <Icon name="PlayIcon" size={16} />
            Play Now
          </button>
          <Link href={`/film-detail-page?id=${film.id}`} className="btn-secondary text-sm sm:text-base px-4 sm:px-6 py-2.5 sm:py-3">
            <Icon name="InformationCircleIcon" size={16} />
            <span className="hidden sm:inline">More Info</span>
            <span className="sm:hidden">Info</span>
          </Link>
          <button className="btn-ghost px-3 sm:px-4 py-2.5 sm:py-3">
            <Icon name="PlusIcon" size={16} />
            <span className="hidden sm:inline">My List</span>
          </button>
        </div>

        {/* Director credit */}
        {film.directorName && (
          <p className="text-xs text-muted-foreground mt-3 sm:mt-4">
            Directed by <span className="text-foreground font-medium">{film.directorName}</span>
          </p>
        )}
      </div>
      {/* Slide indicators */}
      {heroFilms.length > 1 && (
        <div className="absolute bottom-4 sm:bottom-6 right-4 sm:right-6 lg:right-10 2xl:right-16 z-10 flex items-center gap-2">
          {heroFilms.map((_, i) =>
            <button
              key={`hero-dot-${i}`}
              onClick={() => { setTransitioning(true); setTimeout(() => { setActiveIndex(i); setTransitioning(false); }, 300); }}
              className={`rounded-full transition-all duration-300 ${i === activeIndex ? 'w-5 sm:w-6 h-2 bg-primary' : 'w-2 h-2 bg-white/30 hover:bg-white/50'}`}
              aria-label={`Go to slide ${i + 1}`} />
          )}
        </div>
      )}
    </div>
  );
}
